import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useBlocker } from 'react-router';

import { ApiError } from '@shared/api/client';
import { ConfirmDialog } from '@shared/ui/dialog/ConfirmDialog';
import { fieldErrorsFrom, hasFieldErrors } from '@shared/api/field-errors';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { Alert } from '@shared/ui/Alert';
import { cn } from '@shared/ui/cn';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { ArtworkPurposeTable, UploadZone } from '../_shared/components';
import { resolveContentType } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import {
  EMPTY_TITLE_FORM,
  outstandingRequirements,
  titleFormSchema,
  toTitleWriteBody,
  type TitleFormValues,
} from '../_shared/forms.schema';
import {
  contentItemIdOf,
  useApplyExternalMetadata,
  useCreateContentItem,
  useLookupExternalMetadata,
  useUpdateContentItem,
  useUploadCover,
} from '../_shared/mutations';
import {
  requiredArtworkFloor,
  toAcceptAttribute,
  useCategories,
  useContentTypeSchema,
  useTags,
  useUploadPolicy,
  vocabularyOf,
} from '../_shared/queries';
import { CONTENT_TYPES, EXTERNAL_METADATA_PROVIDER, REQUIRED_METADATA } from '../_shared/catalog-settings';
import { TitleFields } from '../_shared/TitleFields';
import type { ExternalCandidate } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { ProviderPanel } from './ContentNew.ProviderPanel';
import { PublishChecklist } from './ContentNew.PublishChecklist';

/**
 * `/content/new` — operator authoring.
 *
 * **The type is the first field, and that is the schema rather than a layout
 * preference.** Type-specific metadata is validated against the type's own
 * schema and `GROUP_SEMANTICS` differs between the two, so the type decides what
 * the rest of the form and the assets surface beneath it will hold. Choosing it
 * last would mean filling a form whose shape had not been decided.
 *
 * **A new title is always a draft, and the form carries no state control.** A
 * selector with one legitimate value is not a choice; publishing, scheduling,
 * and unpublishing all live on the content list. The lede says so and the
 * confirmation repeats it.
 *
 * **The save is one action, and it does not gate on the required fields.** A
 * draft is allowed to be incomplete — the rail says what remains, and the
 * requirement binds publication rather than saving.
 *
 * **The provider lookup is scoped to a title that exists**, so pressing «ابحث»
 * saves the draft first. That is stated in the panel before the press rather
 * than discovered after it, and it is why the surface holds a draft id: every
 * write after the first is a `PATCH` against the same record rather than a
 * second draft.
 */
export function ContentNewPage() {
  const toast = useToast();

  const categories = useCategories();
  const tags = useTags();
  const typeSchema = useContentTypeSchema();
  const uploadPolicy = useUploadPolicy();
  // The shape and the floor a cover must clear, READ off the published policy.
  // Stated under the upload zone, so the requirement is met before the transfer
  // rather than learned from the refusal that ends it.
  const coverFloor = requiredArtworkFloor(uploadPolicy.data);

  const createItem = useCreateContentItem();
  const updateItem = useUpdateContentItem();
  const lookup = useLookupExternalMetadata();
  const applyFill = useApplyExternalMetadata();
  const uploadCover = useUploadCover();

  const contentTypes = CONTENT_TYPES;
  const requiredMetadata = REQUIRED_METADATA;

  const [contentType, setContentType] = useState<string>('');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [hasCover, setHasCover] = useState(false);
  const [savedTitle, setSavedTitle] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // What the service refused, by field. Drawn beside the control that caused it;
  // the page-level line is kept for a refusal that belongs to no single field.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [coverError, setCoverError] = useState<string | null>(null);
  // Accepted, but not visible yet — a different statement from a refusal.
  const [coverNotice, setCoverNotice] = useState<string | null>(null);

  const [providerQuery, setProviderQuery] = useState('');
  const [candidates, setCandidates] = useState<ExternalCandidate[] | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);
  const [providerNotice, setProviderNotice] = useState<string | null>(null);
  const [appliedNote, setAppliedNote] = useState<string | null>(null);

  const doneRef = useRef<HTMLElement>(null);

  const form = useForm<TitleFormValues>({
    resolver: zodResolver(titleFormSchema),
    defaultValues: EMPTY_TITLE_FORM,
  });
  const values = form.watch();

  // **Leaving with unsaved work asks first.** The authoring form is the longest
  // action in the console and it held everything in memory: a stray click on the
  // sidebar discarded a fully-typed title with no dialog, no `beforeunload`, and
  // nothing to return to. The guard lifts the moment the draft is saved, so the
  // confirmation surface is reachable without a prompt.
  const isDirty = form.formState.isDirty && savedTitle === null;
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  // The first type the setting declares is the one the switch opens on; the
  // membership is the setting's, never a pair written here.
  const activeType = contentType || contentTypes[0] || '';
  const outstanding = outstandingRequirements(requiredMetadata, values, hasCover);

  function failureMessage(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    return resolveErrorCopy(code, status, errors).message;
  }

  /** Creates the draft on the first write and `PATCH`es it on every later one. */
  async function persist(): Promise<string | null> {
    const body = toTitleWriteBody(form.getValues());

    try {
      if (draftId) {
        await updateItem.mutateAsync({ contentItemId: draftId, body });
        return draftId;
      }

      const created = await createItem.mutateAsync({ contentType: activeType, body });
      const createdId = contentItemIdOf(created);
      if (!createdId) {
        setFormError(resolveErrorCopy(undefined, 0, errors).message);
        return null;
      }
      setDraftId(createdId);
      return createdId;
    } catch (error) {
      setFieldErrors(fieldErrorsFrom(error));
      // A refusal that named its fields has already been said, beside each of
      // them. Repeating it page-wide says the same thing twice and buries which
      // of the two the operator should read.
      setFormError(hasFieldErrors(error) ? null : failureMessage(error));
      return null;
    }
  }

  /**
   * Save, refusing first anything the schema can refuse.
   *
   * **Routed through `handleSubmit` rather than a bare `trigger()`.** The invalid
   * branch is handed the errors object directly; reading `form.formState.errors`
   * straight after an awaited `trigger()` reads the proxy bound to the render that
   * built this closure, which is still the *previous* pass — so the first press
   * refused the submit and displayed nothing, and the message for it only appeared
   * beside the *next* press.
   */
  const onSave = form.handleSubmit(
    async () => {
      setFormError(null);
      setFieldErrors({});

      const id = await persist();
      if (!id) return;

      setSavedTitle(form.getValues().title);
      // Focus moves into the confirmation, so a reader is not left at a button
      // that has just left the document.
      window.setTimeout(() => doneRef.current?.focus(), 0);
    },
    (invalid) => {
      setFormError(null);
      setFieldErrors(clientFieldErrors(invalid));
    },
  );

  /** The Arabic copy for each rule the schema names, keyed by the schema's own code. */
  function clientFieldErrors(errors_: typeof form.formState.errors): Record<string, string> {
    const mapped: Record<string, string> = {};
    for (const [name, entry] of Object.entries(errors_)) {
      const code = (entry as { message?: string } | undefined)?.message;
      if (!code) continue;
      mapped[name] = copy.validation[code as keyof typeof copy.validation] ?? copy.validation.required;
    }
    return mapped;
  }

  async function onProviderSearch() {
    setProviderNotice(null);
    setAppliedNote(null);

    if (form.getValues().title.trim() === '') {
      setProviderNotice(copy.provider.needsTitle);
      return;
    }

    const id = await persist();
    if (!id) return;

    try {
      const matches = await lookup.mutateAsync({ contentItemId: id, query: providerQuery });
      setCandidates(matches);
      setSelectedCandidate(matches[0]?.external_id ?? null);
    } catch (error) {
      // The provider is an accelerator: the panel states the failure and the
      // form beside it stays completely usable by hand.
      setCandidates(null);
      setProviderNotice(failureMessage(error));
    }
  }

  async function onProviderApply() {
    if (!draftId || !selectedCandidate) return;
    setProviderNotice(null);

    try {
      const result = await applyFill.mutateAsync({
        contentItemId: draftId,
        externalId: selectedCandidate,
      });

      // The values land in the real inputs; every one stays editable.
      const detail = await updateItem.mutateAsync({
        contentItemId: draftId,
        body: {},
      });

      form.reset({
        title: detail.title,
        description: detail.description,
        categoryId: detail.category_id ?? '',
        tagIds: detail.tag_ids ?? [],
        productionYear:
          detail.metadata.production_year === null ? '' : String(detail.metadata.production_year),
        producingCountry: detail.metadata.producing_country ?? '',
        ageRating: detail.metadata.age_rating ?? '',
      });
      setHasCover(detail.cover.url !== null);
      setAppliedNote(copy.provider.applied(result.applied.length));
    } catch (error) {
      setProviderNotice(failureMessage(error));
    }
  }

  async function onCoverFiles(files: FileList) {
    const file = files[0];
    if (!file) return;

    setCoverError(null);
    setCoverNotice(null);

    const id = await persist();
    if (!id) return;

    try {
      // `derived` is the crop landing, not the transfer: the checklist counts a
      // cover the title actually holds, and the upload answers only once the
      // owning service has been given one.
      const result = await uploadCover.mutateAsync({ contentItemId: id, file });
      setHasCover(result.derived);
      setCoverNotice(result.derived ? null : copy.cover.notDerived);
      toast.show(copy.cover.uploaded, 'success');
    } catch (error) {
      // **Under the cover control, not in a toast.** A rejected image is a
      // rejection of the image: reporting it page-wide is what leaves an
      // operator re-doing everything else to fix one file.
      setCoverError(failureMessage(error));
    }
  }

  if (savedTitle !== null && draftId !== null) {
    return (
      <>
        <Breadcrumb
          items={[
            { label: sharedCopy.crumbs.console, to: '/' },
            { label: sharedCopy.crumbs.content, to: '/content' },
            { label: copy.crumb },
          ]}
        />

        {/* The confirmation replaces the form rather than standing beside it. */}
        <section
          ref={doneRef}
          tabIndex={-1}
          aria-labelledby="authoring-done"
          className="mt-6 grid max-w-measure gap-5"
        >
          <Alert kind="success" title={copy.done.heading}>
            <span id="authoring-done">
              {copy.done.bodyLead}
              {savedTitle}
              {copy.done.bodyTail}
            </span>
          </Alert>

          <p className="mb-0 max-w-measure text-sm text-ink-muted">{copy.done.next}</p>

          <div className="flex flex-wrap gap-3">
            <Link
              to={`/content/${draftId}/assets`}
              className="hit-target flex items-center rounded-sm bg-accent px-5 text-sm font-semibold text-accent-ink no-underline hover:bg-accent-hover"
            >
              {copy.done.toAssets}
            </Link>
            <Link
              to={`/content/${draftId}/edit`}
              className="hit-target flex items-center rounded-sm border border-border-strong bg-surface-raised px-5 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay"
            >
              {copy.done.toEdit}
            </Link>
            <Link
              to="/content"
              className="hit-target flex items-center rounded-sm px-5 text-sm text-ink no-underline hover:bg-hover-bg"
            >
              {copy.done.toList}
            </Link>
          </div>
        </section>
      </>
    );
  }

  const busy = createItem.isPending || updateItem.isPending;

  return (
    <>
      <ConfirmDialog
        open={blocker.state === 'blocked'}
        subject={blocker.state === 'blocked' ? copy.leaveGuard : null}
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
      <Breadcrumb
        items={[
          { label: sharedCopy.crumbs.console, to: '/' },
          { label: sharedCopy.crumbs.content, to: '/content' },
          { label: copy.crumb },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">
          {copy.head.ledeLead} <b className="font-semibold text-ink">{copy.head.ledeStrong}</b>{' '}
          {copy.head.ledeTail}
        </p>
      </div>

      <p className="mb-6 max-w-measure text-xs text-ink-muted">{sharedCopy.requiredNote}</p>

      {formError ? (
        <p role="alert" className="mb-6 max-w-measure text-sm text-danger">
          {formError}
        </p>
      ) : null}

      {/*
        The type stands outside the two columns and spans the width: it is the
        condition the rest of the form hangs on, not one of six equal sections.
      */}
      <section aria-labelledby="content-type" className="mb-8 grid gap-3">
        <h2 id="content-type" className="font-display text-h3 font-bold">
          {copy.type.heading}
        </h2>

        <div role="group" aria-label={copy.type.label} className="flex flex-wrap gap-2">
          {contentTypes.map((member) => (
            <button
              key={member}
              type="button"
              aria-pressed={activeType === member}
              onClick={() => setContentType(member)}
              className={cn(
                'hit-target rounded-pill border px-4 text-sm',
                activeType === member
                  ? 'border-accent bg-accent-soft text-accent-text'
                  : 'border-border bg-surface-raised text-ink-muted hover:bg-hover-bg',
              )}
            >
              {resolveContentType(member)}
            </button>
          ))}
        </div>

        {copy.type.notes[activeType] ? (
          <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.type.notes[activeType]}</p>
        ) : null}
      </section>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void onSave();
        }}
        className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-9"
      >
        {/* The column carries what the operator writes, in the order they write it. */}
        <div className="grid gap-8">
          <ProviderPanel
            provider={EXTERNAL_METADATA_PROVIDER}
            query={providerQuery}
            candidates={candidates}
            selected={selectedCandidate}
            busy={lookup.isPending || busy}
            applying={applyFill.isPending}
            notice={providerNotice}
            appliedNote={appliedNote}
            boundExternalId={selectedCandidate}
            referenceUrl={null}
            onQuery={setProviderQuery}
            onSearch={() => void onProviderSearch()}
            onSelect={setSelectedCandidate}
            onApply={() => void onProviderApply()}
          />

          <TitleFields
            register={form.register}
            control={form.control}
            categories={categories.data?.items ?? []}
            tags={tags.data?.items ?? []}
            ageRatings={vocabularyOf(typeSchema.data, activeType, 'age_rating')}
            fieldErrors={fieldErrors}
          />

          <section aria-labelledby="new-credits" className="grid gap-3">
            <h2 id="new-credits" className="font-display text-h3 font-bold">
              {copy.credits.heading}
            </h2>
            <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.credits.lede}</p>
            {/*
              A credit attaches to a record, so it is added once the title has
              one — stated here rather than drawn as a control that would fail.
            */}
            <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.credits.needsDraft}</p>
          </section>
        </div>

        {/*
          The rail carries what the operator reads: the cover it uploads once and
          sees afterwards, and the list that counts what is still outstanding. On
          a phone it falls after the column, which is where a summary belongs.
        */}
        <aside aria-label={copy.checklist.heading} className="grid gap-6">
          <section aria-labelledby="new-cover" className="grid gap-3">
            <h2 id="new-cover" className="font-display text-h3 font-bold">
              {copy.cover.heading}
              <span aria-hidden="true" className="ms-1 text-danger">
                {sharedCopy.requiredMark}
              </span>
            </h2>

            <UploadZone
              label={copy.cover.dropLabel}
              accept={toAcceptAttribute(uploadPolicy.data?.accepted_artwork_formats)}
              hint={copy.cover.hint(
                (uploadPolicy.data?.accepted_artwork_formats ?? []).join(' · '),
                coverFloor?.ratio ?? '',
                // A pixel floor is a MACHINE figure on a human line, and the
                // digits follow the surface: this page is Arabic.
                coverFloor
                  ? `${formatNumber(coverFloor.minWidth)}×${formatNumber(coverFloor.minHeight)}`
                  : '',
              )}
              error={coverError}
              onFiles={(files) => void onCoverFiles(files)}
            />

            {uploadCover.isPending ? (
              <p role="status" className="mb-0 max-w-none text-xs text-ink-muted">
                {copy.cover.uploading}
              </p>
            ) : null}

            {coverNotice !== null ? (
              <p role="status" className="mb-0 max-w-none text-xs text-warning">
                {coverNotice}
              </p>
            ) : null}

            {/* One upload produces four purposes; the table is what shows that. */}
            <ArtworkPurposeTable
              rows={(uploadPolicy.data?.artwork_purposes ?? []).map((purpose) => ({
                purpose: purpose.purpose,
                ratio: purpose.ratio,
                source: purpose.required ? (hasCover ? 'UPLOADED' : 'MISSING') : 'DERIVED',
              }))}
            />
            <p className="mb-0 max-w-none text-xs text-ink-muted">{sharedCopy.artwork.note}</p>
          </section>

          <PublishChecklist requiredMetadata={requiredMetadata} outstanding={outstanding} />
        </aside>

        {/*
          **Full-width stack below `--bp-sm`, one row from it** — the UI/UX
          pack's `[data-form-actions] .btn` rule, whose stated reason is that two
          buttons sharing a line on a 320 px window drop below the 2.75 rem touch
          target. Not centred at any tier: the primary action sits at the
          reading-start edge on every form in this console.
        */}
        <div className="grid gap-3 sm:flex sm:flex-wrap lg:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-sm bg-accent px-5 py-3 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
          >
            {busy ? copy.actions.saving : copy.actions.save}
          </button>
          <Link
            to="/content"
            className="hit-target flex w-full items-center justify-center rounded-sm px-5 text-sm text-ink no-underline hover:bg-hover-bg sm:w-auto"
          >
            {copy.actions.cancel}
          </Link>
        </div>
      </form>
    </>
  );
}
